import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Field } from 'components/uikit/Field';
import { HookFormDropDownInput } from 'components/uikit/inputs/dropdown/HookFormDropDownInput';
import { HookFormMultiSelectDropDownInput } from 'components/uikit/inputs/dropdown/HookFormMultiSelectDropDownInput';
import {
  Button,
  ButtonColor,
  ButtonWidth,
} from 'components/uikit/buttons/Button';
import { HookFormDatePicker } from 'components/uikit/inputs/date-time/HookFormDatePicker';
import { Input } from 'components/uikit/inputs/Input';
import { HookFormTimePicker } from 'components/uikit/inputs/date-time/HookFormTimePicker';
import { requiredRule } from 'helpers/form/react-hook-form-helper';
import { Links } from 'application/constants/links';
import { AppLink } from 'components/uikit/buttons/AppLink';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import styles from './UiKitPage.module.scss';
import notificationsStyles from '../notifications/NotificationModal.module.scss';
import { useModal } from '../../../components/uikit/modal/useModal';
import { PopperExample } from './components/PopperExample';
import { MultiSelectDropDownInput } from 'components/uikit/inputs/dropdown/MultiSelectDropDownInput';
import { HookFormComboBoxInput } from '../../../components/uikit/inputs/dropdown/HookFormComboBoxInput';
import { TimePicker } from 'components/uikit/inputs/date-time/TimePicker';
import { Breadcrumbs } from 'components/uikit/Breadcrumbs';
import PrintIcon from '../../../assets/icons/print-icon.svg';
import PrismaCloud from '../../../assets/icons/prisma-cloud.svg';
import { Checkbox } from '../../../components/uikit/inputs/Checkbox.tsx';
import { Chip } from '../../../components/uikit/pagination/Chip.tsx';
import { Step } from '../../../components/uikit/Step.tsx';
import { Tab } from '../../../components/uikit/Tab.tsx';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { ActionTextWithIcon } from '../../../components/uikit/ActionTextWithIcon.tsx';
import { ListItem } from '../../../components/uikit/ListItem.tsx';
import { Tip } from '../../../components/uikit/Tip.tsx';
import { RadioButton } from '../../../components/uikit/RadioButton.tsx';
import Accepted from '../../../assets/icons/status_accepted.svg';
import Failed from '../../../assets/icons/status_failed.svg';
import Pending from '../../../assets/icons/status_pending.svg';
import Warning from '../../../assets/icons/status_warning.svg';
import i18n from 'i18next';
import clsx from 'clsx';
import HomeIcon from 'assets/icons/home-sweet-home.svg?react';
import ResetIcon from 'assets/icons/reset.svg?react';
import ChevronDown from 'assets/icons/arrow-language-selector.svg?react';
import CloseIcon from 'assets/icons/close-button-small.svg?react';

type UiKitForm = {
  dropDown: string;
  multiSelectDropDown: string[];
  combo: string | string;
  timeInMilliseconds: number;
  date: Date;
  input: string;
  input2: string;
  input3: string;
  input4: string;
  password: string;
  category: string;
};

const categories: { title: string; id: string }[] = [
  { title: 'Auto description', id: 'ProductType.Auto' },
  { title: 'Electronic description', id: 'ProductType.Electronic' },
];
for (let i = 0; i < 100; i++)
  categories.push({
    title: '00000000000000000 000000000000 0000000000000000000 ' + i,
    id: (i + 10) as any,
  });

const TitleTooltipBody = () => {
  return (
    <div
      className={clsx(
        styles.tooltipText,
        styles.rowContainer,
        styles.columnGap10,
      )}
    >
      <span>{i18n.t('Page.PageDesigner.title_tooltip_text')}</span>
      <span className={clsx(styles.rowContainer, styles.columnGap10)}>
        <HomeIcon style={{ height: 18, width: 18 }} />
        <span style={{ marginBottom: -2 }}>
          {' '}
          / {i18n.t('Page.PageDesigner.page_name_example')}
        </span>
      </span>
    </div>
  );
};

export const NOTIFICATION_TYPES = {
  Order_list: 'Order list',
  Outstanding_receivables: 'Outstanding receivables',
  Qualifications: 'Qualifications',
  Repairs: 'Repairs',
  Documents: 'Documents',
  Firmware: 'Firmware',
  Software: 'Software',
  Warranty_request_history: 'Warranty request history',
} as const;

export type NotificationTypeUI =
  (typeof NOTIFICATION_TYPES)[keyof typeof NOTIFICATION_TYPES];

const notificationTypes = Object.values(
  NOTIFICATION_TYPES,
) as NotificationTypeUI[];

export const UiKitPage: React.FC = () => {
  const [selectedTypes, setSelectedTypes] = useState<NotificationTypeUI[]>([]);
  const [isSelectVisible, setIsSelectVisible] = useState(false);

  const i18n = useScopedTranslation('Page.uikit');
  const form = useAdvancedForm<UiKitForm>(async (data) => {
    alert(JSON.stringify(data));
  });
  const options = useMemo(() => {
    return ['ProductType.Auto', 'ProductType.Electronic', 'ProductType.Other'];
  }, []);
  const modals = useModal();
  const [multiValues, setMultiValues] = useState<string[]>([]);

  const isAllChecked = () =>
    notificationTypes.every((notificationType) =>
      selectedTypes.includes(notificationType),
    );

  const removeSelectedType = (selectedType: NotificationTypeUI) => {
    setSelectedTypes((prevState) =>
      prevState.filter((type) => type !== selectedType),
    );
  };

  useEffect(() => {
    const closeSelect = () => {
      setIsSelectVisible(false);
    };

    document.body.addEventListener('click', closeSelect);

    return () => document.body.removeEventListener('click', closeSelect);
  }, []);

  return (
    <div>
      <div className={styles.relative}>
        <div
          className={clsx(styles.selectButton, styles.spaceBetween)}
          onClick={(e) => {
            e.stopPropagation();
            setIsSelectVisible((prev) => !prev);
          }}
        >
          <div className={clsx(styles.rowContainer, styles.columnGap4)}>
            {!selectedTypes.length && (
              <div className={clsx(styles.greyText, styles.marginLeft10)}>
                Choose area
              </div>
            )}
            <div
              className={clsx(
                styles.rowContainer,
                styles.columnGap4,
                styles.chipsContainer,
                selectedTypes.length > 2 && styles.maxWidth264,
              )}
            >
              {selectedTypes.map((selectedType, index) => {
                if (index > 1) {
                  return null;
                }

                return (
                  <div
                    key={selectedType}
                    className={clsx(
                      styles.rowContainer,
                      styles.columnGap4,
                      styles.selectedType,
                    )}
                  >
                    <div className={clsx(styles.nowrap, styles.chipText)}>
                      {selectedType}
                    </div>
                    <div className={styles.flex}>
                      <CloseIcon
                        className={styles.deleteIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSelectedType(selectedType);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedTypes.length > 2 && (
              <div className={styles.text}>
                + {selectedTypes.length - 2} more
              </div>
            )}
          </div>
          <div className={styles.flex}>
            <ChevronDown />
          </div>
        </div>
        {isSelectVisible && (
          <div
            className={notificationsStyles.selectContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={clsx(
                notificationsStyles.selectItem,
                notificationsStyles.selectItemBorderBottom,
              )}
            >
              <Checkbox
                actionText={'Select all'}
                isChecked={isAllChecked()}
                onChange={() => {
                  if (isAllChecked()) {
                    setSelectedTypes([]);
                  } else {
                    setSelectedTypes(notificationTypes);
                  }
                }}
                halfChecked={!!selectedTypes.length}
              />
            </div>
            {notificationTypes.map((notificationType) => (
              <div
                className={clsx(
                  notificationsStyles.selectItem,
                  selectedTypes.includes(notificationType) &&
                    notificationsStyles.selectedItem,
                )}
                key={notificationType}
              >
                <Checkbox
                  actionText={notificationType}
                  isChecked={selectedTypes.includes(notificationType)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTypes((prev) => [...prev, notificationType]);
                    } else {
                      setSelectedTypes((prev) =>
                        prev.filter((item) => item !== notificationType),
                      );
                    }
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <Breadcrumbs />
      <AppLink color={ButtonColor.Primary} to={Links.Authorized.Welcome.link()}>
        Back
      </AppLink>
      <form onSubmit={form.handleSubmitDefault} className={styles.main}>
        <div className={styles.title}>Inputs</div>
        <div className={styles.columnContainer}>
          <Input
            {...form.register('input', requiredRule())}
            errorText={form.formState.errors.input?.message}
            leftHelperText={'Left'}
            rightHelperText={'Right'}
            bottomHelperText={'Bottom'}
            topHelperText={'Top'}
            topRightHelperText={'Required'}
            placeholder={'Placeholder'}
          />
          <Input
            {...form.register('input2', requiredRule())}
            errorText={form.formState.errors.input2?.message}
            leftHelperText={'Left'}
            rightHelperText={'Right'}
            bottomHelperText={'Bottom'}
            placeholder={'Placeholder'}
            disabled={true}
            containerClassName={styles.marginTop40}
          />
          <Input
            {...form.register('input3', requiredRule())}
            errorText={form.formState.errors.input3?.message}
            leftHelperText={'Left'}
            rightHelperText={'Right'}
            bottomHelperText={'Bottom'}
            topHelperText={'Top'}
            placeholder={'Placeholder'}
            isSmallInput={true}
            containerClassName={styles.marginTop40}
          />
          <Input
            {...form.register('input4', requiredRule())}
            errorText={form.formState.errors.input4?.message}
            leftHelperText={'Left'}
            rightHelperText={'Right'}
            bottomHelperText={'Bottom'}
            topHelperText={'Top'}
            placeholder={'Placeholder'}
            isSmallInput={true}
            disabled={true}
            containerClassName={styles.marginTop40}
          />
        </div>
        <Input
          {...form.register('password', requiredRule())}
          type={'password'}
          variant={'formInput'}
          errorText={form.formState.errors.password?.message}
        />
        <div className={styles.title}>Buttons</div>
        <div className={styles.columnContainer}>
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            icon={PrintIcon}
          />
          <Button
            title={'Refresh'}
            className={styles.loadingButton}
            iconClassName={styles.icon}
          >
            <ResetIcon />
          </Button>
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            icon={PrintIcon}
            width={ButtonWidth.Fullwidth}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            icon={PrintIcon}
            disabled={true}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            icon={PrintIcon}
            width={ButtonWidth.Fullwidth}
            disabled={true}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            color={ButtonColor.Secondary}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            width={ButtonWidth.Fullwidth}
            color={ButtonColor.Secondary}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            color={ButtonColor.Secondary}
            disabled={true}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            width={ButtonWidth.Fullwidth}
            color={ButtonColor.Secondary}
            disabled={true}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            color={ButtonColor.External}
            icon={PrismaCloud}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            width={ButtonWidth.Fullwidth}
            color={ButtonColor.External}
            icon={PrismaCloud}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            color={ButtonColor.External}
            icon={PrismaCloud}
            disabled={true}
          />
          <Button
            type={'submit'}
            title={i18n.t('submit_button_title')}
            width={ButtonWidth.Fullwidth}
            color={ButtonColor.External}
            icon={PrismaCloud}
            disabled={true}
          />
        </div>
        <div className={styles.title}>Checkboxes</div>
        <div className={styles.columnContainer}>
          <Checkbox helperText={"Hello, guys, I'm checkbox!"} />
          <Checkbox
            disabled={true}
            helperText={"And I'm his younger brother, Checkbox The Second!"}
          />
          <Checkbox
            disabled={true}
            isChecked={true}
            helperText={'The first pancakes are always failures'}
          />
        </div>
        <div className={styles.title}>Chips, Tips and Steps</div>
        <div className={styles.columnContainer}>
          <Chip value={'100'} isActive={true} />
          <Chip value={'100'} disabled={true} />
          <Step value={1} isActive={true} />
          <Step value={2} />
          <Tip tooltipText={"Hello I'm tooltip"}></Tip>
          <Tip tooltipText={'Success image tooltip'} customImage={Accepted} />
          <Tip tooltipText={'Failed image tooltip'} customImage={Failed} />
          <Tip tooltipText={'Pending image tooltip'} customImage={Pending} />
          <Tip tooltipBody={<TitleTooltipBody />} customImage={Warning} />
          <Tip />
        </div>
        <div className={styles.title}>Tabs and tooltips</div>
        <div className={styles.columnContainer}>
          <Tab text={'You were the chosen one!'} isSelected={false} />
          <Tab text={'The actual chosen one'} isSelected={true} />
          <Tooltip bodyContent={<div>Tooltip text</div>} />
        </div>
        <div className={styles.title}>Texts</div>
        <div className={styles.columnContainer}>
          <ActionText text={'Some text'} />
          <ActionText text={'Some text'} disabled={true} />
          <NegativeActionText text={'Some danger text'} />
          <NegativeActionText text={'Some danger text'} disabled={true} />
          <ActionTextWithIcon
            text={'Action text with icon'}
            icon={PrismaCloud}
          />
          <ActionTextWithIcon
            text={'Action text with icon'}
            icon={PrismaCloud}
            disabled={true}
          />
        </div>
        <div className={styles.title}>Options</div>
        <div className={styles.columnContainer}>
          <ListItem text={'Option 1'} icon={PrismaCloud} isSelected={true} />
          <ListItem text={'Option 2'} />
        </div>
        <div className={styles.title}>Radio Buttons</div>
        <div className={styles.columnContainer}>
          <RadioButton title={'lalalalalala'} />
          <RadioButton title={'lalalalalala'} defaultChecked={true} />
          <RadioButton title={'lalalalalala'} disabled={true} />
          <RadioButton
            title={'lalalalalala'}
            disabled={true}
            defaultChecked={true}
          />
        </div>

        <Field title={i18n.t('dropdown')}>
          <HookFormDropDownInput
            options={options}
            customOptions={[
              {
                label: 'Add new',
                onClick: () => {
                  alert('Adding new ProductType is not implemented');
                },
              },
            ]}
            name={'dropDown'}
            control={form.control}
          />
        </Field>
        <Field title={i18n.t('hook_form_dropdown_complex_option')}>
          <HookFormDropDownInput
            options={categories}
            useVirtualization={true}
            popupWidth={'autosize'}
            name={'category'}
            getOptionLabel={'title'}
            idFunction={(x) => x.id}
            control={form.control}
          />
        </Field>
        <Field title={i18n.t('multi_select_dropdown')}>
          <HookFormMultiSelectDropDownInput
            options={options}
            name={'multiSelectDropDown'}
            control={form.control}
            hasSearchFilter={true}
          />
        </Field>
        <Field title="Non-hook-form">
          <MultiSelectDropDownInput
            options={options}
            hasSearchFilter={true}
            value={multiValues}
            variant={'formInput'}
            headerTitle={'Test'}
            onValueChanged={(newValues) => setMultiValues(newValues as any)}
          />
        </Field>
        <Field title="Combo">
          <HookFormComboBoxInput
            name={'combo'}
            control={form.control}
            options={options}
            variant={'formInput'}
          />
        </Field>
        <Field title={i18n.t('date')}>
          <HookFormDatePicker name={'date'} control={form.control} />
        </Field>
        <Field title={i18n.t('time')}>
          <HookFormTimePicker
            name={'timeInMilliseconds'}
            control={form.control}
          />
        </Field>
        <Field title={i18n.t('time')}>
          <TimePicker timeInMills={120000} />
        </Field>
      </form>
      <Button
        color={ButtonColor.Primary}
        onClick={async () => {
          const result = await modals.showAlert({
            text: 'zxc',
            title: 'qwe',
          });
          alert(result);
        }}
        title={'Show alert'}
      ></Button>
      <Button
        color={ButtonColor.Primary}
        onClick={async () => {
          const result = await modals.showConfirm({
            text: 'zxc',
            title: 'qwe',
          });
          alert(result);
        }}
        title={'Show confirm'}
      ></Button>
      <Button
        color={ButtonColor.Secondary}
        onClick={async () => {
          const result = await modals.showPrompt({
            text: 'zxc',
            title: 'qwe',
            defaultValue: '',
            fieldName: 'Project Name',
          });
          alert(result);
        }}
        title={'Show prompt'}
      ></Button>
      <PopperExample />
    </div>
  );
};
